import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Bath, Bed, BedDouble, Car, ExternalLink, Map as MapIcon, Star, Users } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Dresden coordinates as reference point
const DRESDEN_COORDS = [51.0504, 13.7373];

// Fix for default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const parseImages = (imagesJson) => {
  if (!imagesJson) return [];
  try {
    const parsed = JSON.parse(imagesJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatEuro = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return null;
  const total = Math.max(0, Math.round(Number(minutes)));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours === 0) return `${rest} Min.`;
  if (rest === 0) return `${hours} Std.`;
  return `${hours} Std. ${rest} Min.`;
};

// Custom icons for different availability statuses
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 2px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          width: 8px;
          height: 8px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });
};

const availabilityColors = {
  available: '#059669',
  unavailable: '#e11d48',
  unknown: '#d97706',
};

const availabilityMeta = {
  available: { label: 'Verfügbar', dot: '#059669' },
  unavailable: { label: 'Nicht verfügbar', dot: '#e11d48' },
  unknown: { label: 'Unklar', dot: '#d97706' },
};

const createDepartureIcon = () =>
  L.divIcon({
    className: 'departure-marker',
    html: `
      <div style="
        background-color: #2563eb;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.35);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 8px;
          height: 8px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });

function FitBounds({ listings, departure }) {
  const map = useMap();

  useEffect(() => {
    if (!listings || listings.length === 0) return;

    const positions = listings.map((listing) => [listing.lat, listing.lng]);
    if (departure) positions.push([departure.lat, departure.lng]);

    if (positions.length === 1) {
      map.setView(positions[0], 12);
    } else {
      map.fitBounds(L.latLngBounds(positions), { padding: [48, 48], maxZoom: 13 });
    }
  }, [listings, departure?.lat, departure?.lng, map]);

  return null;
}

function MapPlaceholder({ title, subtitle }) {
  return (
    <div className="relative h-[420px] w-full overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100">
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-4 text-center">
        <MapIcon size={24} strokeWidth={1.5} className="text-neutral-300" />
        <p className="text-sm font-medium text-neutral-600">{title}</p>
        <p className="text-xs text-neutral-400">{subtitle}</p>
      </div>
    </div>
  );
}

function MapView({ listings, trip, travelTimes = {} }) {
  const [geocodedListings, setGeocodedListings] = useState([]);
  const [center, setCenter] = useState(DRESDEN_COORDS);
  const [loading, setLoading] = useState(true);

  const departurePosition =
    trip?.departure_lat != null && trip?.departure_lng != null
      ? { lat: Number(trip.departure_lat), lng: Number(trip.departure_lng) }
      : null;

  useEffect(() => {
    const geocodeListings = async () => {
      const results = [];

      for (const listing of listings) {
        let lat = null;
        let lng = null;

        // 1. Prefer exact coordinates scraped from Airbnb
        if (listing.lat != null && listing.lng != null &&
            listing.lat >= -90 && listing.lat <= 90 &&
            listing.lng >= -180 && listing.lng <= 180) {
          lat = Number(listing.lat);
          lng = Number(listing.lng);
        }
        // 2. Fallback: geocode the location text
        else if (listing.location) {
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(listing.location)}&limit=1`,
              { headers: { 'Accept-Language': 'de' } }
            );
            const data = await response.json();

            if (data && data.length > 0) {
              lat = parseFloat(data[0].lat);
              lng = parseFloat(data[0].lon);
            }
          } catch (error) {
            console.error('Geocoding error:', error);
          }
        }

        // 3. Last resort: only include listings without any position if nothing found
        results.push({
          ...listing,
          lat: lat ?? DRESDEN_COORDS[0],
          lng: lng ?? DRESDEN_COORDS[1],
          hasExactPosition: lat != null && lng != null,
        });
      }

      setGeocodedListings(results);
      setLoading(false);

      // Calculate center based on all locations
      if (results.length > 0) {
        const validResults = results.filter(r => r.lat !== DRESDEN_COORDS[0] || r.lng !== DRESDEN_COORDS[1]);
        if (validResults.length > 0) {
          const avgLat = validResults.reduce((sum, r) => sum + r.lat, 0) / validResults.length;
          const avgLng = validResults.reduce((sum, r) => sum + r.lng, 0) / validResults.length;
          setCenter([avgLat, avgLng]);
        } else {
          setCenter(DRESDEN_COORDS);
        }
      }
    };

    if (listings && listings.length > 0) {
      geocodeListings();
    } else {
      setLoading(false);
    }
  }, [listings]);

  if (loading) {
    return <MapPlaceholder title="Karte wird geladen" subtitle="Unterkünfte werden angezeigt." />;
  }

  if (!listings || listings.length === 0) {
    return <MapPlaceholder title="Keine Unterkünfte" subtitle="Füge zuerst Airbnb-Links hinzu." />;
  }

  return (
    <div className="h-[420px] w-full overflow-hidden rounded-lg border border-neutral-200">
      <MapContainer
        center={center}
        zoom={10}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={true}
        className="z-0"
      >
        <FitBounds listings={geocodedListings} departure={departurePosition} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {geocodedListings.map((listing) => {
          const status = availabilityMeta[listing.availability_status] || availabilityMeta.unknown;
          return (
            <Marker
              key={listing.id}
              position={[listing.lat, listing.lng]}
              icon={createCustomIcon(availabilityColors[listing.availability_status] || availabilityColors.unknown)}
            >
              <Popup>
                <div className="w-56 space-y-2">
                  {listing.image && (
                    <img
                      src={listing.image}
                      alt={listing.title}
                      className="h-28 w-full rounded-md object-cover"
                    />
                  )}
                  <h4 className="text-sm font-semibold leading-snug text-neutral-900">
                    {listing.title || 'Unterkunft'}
                  </h4>
                  {parseImages(listing.images_json).length > 0 && (
                    <p className="text-xs text-neutral-400">
                      {parseImages(listing.images_json).length} Bilder
                    </p>
                  )}
                  {listing.price_night && (
                    <div className="space-y-0.5">
                      <p className="text-sm font-semibold text-neutral-900">
                        {formatEuro(listing.price_night)}{' '}
                        <span className="text-xs font-normal text-neutral-500">Ø / Nacht</span>
                      </p>
                      {listing.guests && listing.guests > 0 && (
                        <p className="text-xs text-neutral-500">
                          {formatEuro(listing.price_night / listing.guests)} pro Person / Nacht
                        </p>
                      )}
                      {listing.price_total && (
                        <p className="text-xs text-neutral-500">
                          {formatEuro(listing.price_total)} gesamt
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-600">
                    {listing.guests && (
                      <span className="inline-flex items-center gap-1">
                        <Users size={12} /> {listing.guests}
                      </span>
                    )}
                    {listing.bedrooms && (
                      <span className="inline-flex items-center gap-1">
                        <BedDouble size={12} /> {listing.bedrooms}
                      </span>
                    )}
                    {listing.beds && (
                      <span className="inline-flex items-center gap-1">
                        <Bed size={12} /> {listing.beds}
                      </span>
                    )}
                    {listing.bathrooms && (
                      <span className="inline-flex items-center gap-1">
                        <Bath size={12} /> {listing.bathrooms.toFixed(1).replace('.0', '').replace('.', ',')}
                      </span>
                    )}
                  </div>
                  {listing.rating && (
                    <p className="inline-flex items-center gap-1 text-xs text-neutral-700">
                      <Star size={12} className="fill-amber-400 text-amber-400" /> {listing.rating} (
                      {listing.review_count} Bewertungen)
                    </p>
                  )}
                  {travelTimes[listing.id]?.minutes != null && (
                    <p className="inline-flex items-center gap-1 text-xs font-medium text-neutral-700">
                      <Car size={12} /> {formatDuration(travelTimes[listing.id].minutes)} ab{' '}
                      {trip?.departure || 'Abfahrtsort'}
                      {travelTimes[listing.id].km != null
                        ? ` · ${Number(travelTimes[listing.id].km).toLocaleString('de-DE', {
                            maximumFractionDigits: 1,
                          })} km`
                        : ''}
                    </p>
                  )}
                  <div>
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs font-medium text-neutral-700">
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: status.dot }}
                      />
                      {status.label}
                    </span>
                  </div>
                  <a
                    href={listing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-neutral-900 px-3 text-xs font-medium text-white transition hover:bg-neutral-700"
                  >
                    Auf Airbnb ansehen <ExternalLink size={12} />
                  </a>
                </div>
              </Popup>
            </Marker>
          );
        })}
        {departurePosition && (
          <Marker
            position={[departurePosition.lat, departurePosition.lng]}
            icon={createDepartureIcon()}
            zIndexOffset={500}
          >
            <Popup>
              <div className="w-44">
                <h4 className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-900">
                  <Car size={14} /> Abfahrtsort
                </h4>
                <p className="mt-1 text-xs text-neutral-500">{trip?.departure || 'Abfahrtsort'}</p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}

export default MapView;
